import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
    collection,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    query,
    where,
    orderBy,
    limit,
    addDoc,
    deleteDoc
} from "firebase/firestore";
import {
    Search,
    Filter,
    Tag,
    MapPin,
    Bookmark,
    ChevronDown,
    X,
    Menu,
    RefreshCw,
    PieChart,
    Award,
    Star,
    Info,
    Grid,
    List,
    BadgeCheck,
    Map,
    ChevronLeft,
    ChevronRight,
    Sliders,
    Coffee,
    Heart,
    Clock,
    ArrowUp,
    Phone,
    Edit,
    Save,
    Trash,
    Plus
} from 'lucide-react';
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

const RestBack = () => {
    // Estados para datos
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [filteredRestaurants, setFilteredRestaurants] = useState([]);

    // Estados para filtros
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados para UI
    const [showFilters, setShowFilters] = useState(false);
    const [selectedTab, setSelectedTab] = useState("all");
    const [viewMode, setViewMode] = useState("grid");
    const [stats, setStats] = useState({
        totalRestaurants: 0,
        totalCategories: 0,
        totalSubcategories: 0
    });
    const [favorites, setFavorites] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(8);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showCategoryFilter, setShowCategoryFilter] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // Estados para edición
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedRestaurant, setEditedRestaurant] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [showMapPicker, setShowMapPicker] = useState(false);

    // Estado para la API de Google Maps
    const [mapCenter, setMapCenter] = useState({
        lat: 20.6534, // Centro por defecto (México)
        lng: -103.3919
    });
    const [mapZoom, setMapZoom] = useState(15);
    const [tempMarkerPosition, setTempMarkerPosition] = useState(null);

    // Configuración del mapa
    const mapContainerStyle = {
        width: "100%",
        height: "300px"
    };

    // Detector de scroll para el botón de volver arriba
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Cargar datos de Firebase
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                // Obtener categorías
                const categoriesSnapshot = await getDocs(
                    query(collection(db, "categories"), orderBy("name"))
                );
                const categoriesData = categoriesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setCategories(categoriesData);

                // Obtener subcategorías
                const subcategoriesSnapshot = await getDocs(
                    query(collection(db, "subcategories"), orderBy("name"))
                );
                const subcategoriesData = subcategoriesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setSubcategories(subcategoriesData);

                // Obtener restaurantes
                const restaurantsSnapshot = await getDocs(
                    query(collection(db, "restaurants"), orderBy("name"))
                );
                const restaurantsData = restaurantsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    isFavorite: false
                }));

                // Enriquecer datos - añadir nombres de categorías y subcategorías
                const enrichedRestaurantsData = restaurantsData.map(restaurant => {
                    // Obtener nombres de categorías
                    const categoryNames = restaurant.categoryIds?.map(catId => {
                        const category = categoriesData.find(cat => cat.id === catId);
                        return category ? category.name : null;
                    }).filter(Boolean) || [];

                    // Obtener nombres de subcategorías
                    const subcategoryNames = restaurant.subcategoryIds?.map(subId => {
                        const subcategory = subcategoriesData.find(sub => sub.id === subId);
                        return subcategory ? subcategory.name : null;
                    }).filter(Boolean) || [];

                    return {
                        ...restaurant,
                        categoryNames,
                        subcategoryNames,
                        // Valores por defecto para campos que pueden no existir
                        phone: restaurant.phone || "",
                        schedule: restaurant.schedule || "",
                        location: restaurant.location || {
                            lat: 20.6534,
                            lng: -103.3919
                        }
                    };
                });

                setRestaurants(enrichedRestaurantsData);
                setFilteredRestaurants(enrichedRestaurantsData);

                // Actualizar estadísticas
                setStats({
                    totalRestaurants: enrichedRestaurantsData.length,
                    totalCategories: categoriesData.length,
                    totalSubcategories: subcategoriesData.length
                });

                // Cargar favoritos del local storage
                const storedFavorites = localStorage.getItem('restaurantFavorites');
                if (storedFavorites) {
                    const parsedFavorites = JSON.parse(storedFavorites);
                    setFavorites(parsedFavorites);

                    // Marcar restaurantes favoritos
                    const updatedRestaurants = enrichedRestaurantsData.map(restaurant => ({
                        ...restaurant,
                        isFavorite: parsedFavorites.includes(restaurant.id)
                    }));
                    setRestaurants(updatedRestaurants);
                    setFilteredRestaurants(updatedRestaurants);
                }

                setIsLoading(false);
            } catch (err) {
                console.error("Error fetching data: ", err);
                setError("Error al cargar los datos: " + err.message);
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Actualizar filteredRestaurants cuando cambien los filtros
    useEffect(() => {
        applyFilters();
        // Reset to first page when filters change
        setCurrentPage(1);
    }, [searchTerm, selectedCategories, selectedSubcategories, selectedTab, restaurants]);

    // Función para aplicar filtros
    const applyFilters = () => {
        let filtered = [...restaurants];

        // Filtrar por término de búsqueda
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(restaurant =>
                restaurant.name.toLowerCase().includes(term)
            );
        }

        // Filtrar por categorías seleccionadas
        if (selectedCategories.length > 0) {
            filtered = filtered.filter(restaurant =>
                restaurant.categoryIds &&
                restaurant.categoryIds.some(id => selectedCategories.includes(id))
            );
        }

        // Filtrar por subcategorías seleccionadas
        if (selectedSubcategories.length > 0) {
            filtered = filtered.filter(restaurant =>
                restaurant.subcategoryIds &&
                restaurant.subcategoryIds.some(id => selectedSubcategories.includes(id))
            );
        }

        // Filtrar por tab seleccionado
        if (selectedTab === "favorites") {
            filtered = filtered.filter(restaurant => restaurant.isFavorite);
        }

        setFilteredRestaurants(filtered);
    };

    // Manejar selección de categoría
    const handleCategorySelect = (categoryId) => {
        setSelectedCategories(prevSelected => {
            if (prevSelected.includes(categoryId)) {
                return prevSelected.filter(id => id !== categoryId);
            } else {
                return [...prevSelected, categoryId];
            }
        });
    };

    // Manejar selección de subcategoría
    const handleSubcategorySelect = (subcategoryId) => {
        setSelectedSubcategories(prevSelected => {
            if (prevSelected.includes(subcategoryId)) {
                return prevSelected.filter(id => id !== subcategoryId);
            } else {
                return [...prevSelected, subcategoryId];
            }
        });
    };

    // Manejar cambio de favorito
    const handleToggleFavorite = (restaurantId, event) => {
        // Prevenir eventos de burbujeo
        if (event) {
            event.stopPropagation();
        }

        // Actualizar lista de restaurantes
        const updatedRestaurants = restaurants.map(restaurant => {
            if (restaurant.id === restaurantId) {
                return {
                    ...restaurant,
                    isFavorite: !restaurant.isFavorite
                };
            }
            return restaurant;
        });
        setRestaurants(updatedRestaurants);

        // Actualizar lista de favoritos
        const updatedFavorites = updatedRestaurants
            .filter(restaurant => restaurant.isFavorite)
            .map(restaurant => restaurant.id);
        setFavorites(updatedFavorites);

        // Guardar en localStorage
        localStorage.setItem('restaurantFavorites', JSON.stringify(updatedFavorites));
    };

    // Limpiar todos los filtros
    const clearAllFilters = () => {
        setSearchTerm("");
        setSelectedCategories([]);
        setSelectedSubcategories([]);
        setShowFilterModal(false);
    };

    // Seleccionar restaurante para editar
    const handleSelectRestaurant = (restaurant) => {
        setSelectedRestaurant(restaurant);
        setEditedRestaurant({
            ...restaurant,
            categoryIds: restaurant.categoryIds || [],
            subcategoryIds: restaurant.subcategoryIds || [],
            phone: restaurant.phone || "",
            schedule: restaurant.schedule || "",
            location: restaurant.location || { lat: 20.6534, lng: -103.3919 }
        });
        setMapCenter(restaurant.location || { lat: 20.6534, lng: -103.3919 });
        setTempMarkerPosition(restaurant.location || null);
        setShowEditModal(true);
    };

    // Manejar cambios en los campos del formulario
    const handleRestaurantFormChange = (field, value) => {
        setEditedRestaurant(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Alternar selección de categoría en el editor
    const handleEditCategoryToggle = (categoryId) => {
        setEditedRestaurant(prev => {
            const currentCategoryIds = prev.categoryIds || [];
            let newCategoryIds;

            if (currentCategoryIds.includes(categoryId)) {
                newCategoryIds = currentCategoryIds.filter(id => id !== categoryId);
            } else {
                newCategoryIds = [...currentCategoryIds, categoryId];
            }

            return {
                ...prev,
                categoryIds: newCategoryIds,
                // Actualizar nombres de categorías para la UI
                categoryNames: categories
                    .filter(cat => newCategoryIds.includes(cat.id))
                    .map(cat => cat.name)
            };
        });
    };

    // Alternar selección de subcategoría en el editor
    const handleEditSubcategoryToggle = (subcategoryId) => {
        setEditedRestaurant(prev => {
            const currentSubcategoryIds = prev.subcategoryIds || [];
            let newSubcategoryIds;

            if (currentSubcategoryIds.includes(subcategoryId)) {
                newSubcategoryIds = currentSubcategoryIds.filter(id => id !== subcategoryId);
            } else {
                newSubcategoryIds = [...currentSubcategoryIds, subcategoryId];
            }

            return {
                ...prev,
                subcategoryIds: newSubcategoryIds,
                // Actualizar nombres de subcategorías para la UI
                subcategoryNames: subcategories
                    .filter(sub => newSubcategoryIds.includes(sub.id))
                    .map(sub => sub.name)
            };
        });
    };

    // Guardar cambios en la base de datos
    const handleSaveRestaurant = async () => {
        try {
            setIsLoading(true);

            if (isCreating) {
                // Crear nuevo restaurante
                await addDoc(collection(db, "restaurants"), {
                    name: editedRestaurant.name,
                    categoryIds: editedRestaurant.categoryIds || [],
                    subcategoryIds: editedRestaurant.subcategoryIds || [],
                    phone: editedRestaurant.phone || "",
                    schedule: editedRestaurant.schedule || "",
                    location: editedRestaurant.location || { lat: 20.6534, lng: -103.3919 }
                });
            } else {
                // Actualizar restaurante existente
                const restaurantRef = doc(db, "restaurants", editedRestaurant.id);
                await updateDoc(restaurantRef, {
                    name: editedRestaurant.name,
                    categoryIds: editedRestaurant.categoryIds || [],
                    subcategoryIds: editedRestaurant.subcategoryIds || [],
                    phone: editedRestaurant.phone || "",
                    schedule: editedRestaurant.schedule || "",
                    location: editedRestaurant.location || { lat: 20.6534, lng: -103.3919 }
                });
            }

            // Actualizar lista de restaurantes
            if (isCreating) {
                // Refrescar todos los datos después de crear
                window.location.reload();
            } else {
                // Actualizar solo en memoria para actualización
                const updatedRestaurants = restaurants.map(restaurant => {
                    if (restaurant.id === editedRestaurant.id) {
                        return {
                            ...editedRestaurant
                        };
                    }
                    return restaurant;
                });
                setRestaurants(updatedRestaurants);
            }

            setShowEditModal(false);
            setSelectedRestaurant(null);
            setEditedRestaurant(null);
            setIsCreating(false);
            setIsLoading(false);

        } catch (err) {
            console.error("Error saving restaurant: ", err);
            setError("Error al guardar los cambios: " + err.message);
            setIsLoading(false);
        }
    };

    // Eliminar restaurante
    const handleDeleteRestaurant = async () => {
        if (!editedRestaurant?.id) return;

        if (window.confirm("¿Estás seguro de que deseas eliminar este restaurante?")) {
            try {
                setIsLoading(true);
                await deleteDoc(doc(db, "restaurants", editedRestaurant.id));

                // Actualizar lista de restaurantes
                const updatedRestaurants = restaurants.filter(
                    restaurant => restaurant.id !== editedRestaurant.id
                );
                setRestaurants(updatedRestaurants);

                setShowEditModal(false);
                setSelectedRestaurant(null);
                setEditedRestaurant(null);
                setIsLoading(false);

            } catch (err) {
                console.error("Error deleting restaurant: ", err);
                setError("Error al eliminar el restaurante: " + err.message);
                setIsLoading(false);
            }
        }
    };

    // Iniciar creación de nuevo restaurante
    const handleCreateNewRestaurant = () => {
        setIsCreating(true);
        setEditedRestaurant({
            name: "",
            categoryIds: [],
            subcategoryIds: [],
            categoryNames: [],
            subcategoryNames: [],
            phone: "",
            schedule: "",
            location: { lat: 20.6534, lng: -103.3919 }
        });
        setMapCenter({ lat: 20.6534, lng: -103.3919 });
        setTempMarkerPosition({ lat: 20.6534, lng: -103.3919 });
        setShowEditModal(true);
    };

    // Manejar clic en el mapa para seleccionar ubicación
    const handleMapClick = (event) => {
        const newPosition = {
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
        };

        setTempMarkerPosition(newPosition);
        setEditedRestaurant(prev => ({
            ...prev,
            location: newPosition
        }));
    };

    // Abrir selector de ubicación en mapa
    const handleOpenMapPicker = () => {
        setShowMapPicker(true);
    };

    // Cerrar selector de ubicación en mapa
    const handleCloseMapPicker = () => {
        setShowMapPicker(false);
    };

    // Scroll to top
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Obtener color aleatorio pero consistente basado en texto
    const getColorForText = (text) => {
        const colors = [
            "bg-pink-100 text-pink-800 border-pink-200",
            "bg-purple-100 text-purple-800 border-purple-200",
            "bg-indigo-100 text-indigo-800 border-indigo-200",
            "bg-blue-100 text-blue-800 border-blue-200",
            "bg-cyan-100 text-cyan-800 border-cyan-200",
            "bg-teal-100 text-teal-800 border-teal-200",
            "bg-emerald-100 text-emerald-800 border-emerald-200",
            "bg-green-100 text-green-800 border-green-200",
            "bg-lime-100 text-lime-800 border-lime-200",
            "bg-yellow-100 text-yellow-800 border-yellow-200",
            "bg-amber-100 text-amber-800 border-amber-200",
            "bg-orange-100 text-orange-800 border-orange-200",
            "bg-red-100 text-red-800 border-red-200"
        ];

        if (!text) return colors[0];

        // Simple hash function para seleccionar color
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
            hash = ((hash << 5) - hash) + text.charCodeAt(i);
            hash = hash & hash;
        }

        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    // Paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredRestaurants.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredRestaurants.length / itemsPerPage);

    const paginate = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            // Scroll to top when changing page
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    // Generar color de fondo para tarjeta
    const getCardGradient = (id) => {
        const gradients = [
            "from-rose-500/10 to-pink-500/10",
            "from-purple-500/10 to-violet-500/10",
            "from-indigo-500/10 to-blue-500/10",
            "from-blue-500/10 to-cyan-500/10",
            "from-teal-500/10 to-emerald-500/10",
            "from-emerald-500/10 to-green-500/10",
            "from-lime-500/10 to-yellow-500/10",
            "from-amber-500/10 to-orange-500/10",
            "from-orange-500/10 to-red-500/10"
        ];

        if (!id) return gradients[0];

        // Use the ID to select a consistent gradient
        const index = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % gradients.length;
        return gradients[index];
    };

    // Renderizar tarjeta de restaurante (vista de cuadrícula)
    const renderRestaurantCard = (restaurant) => {
        const gradientClass = getCardGradient(restaurant.id);

        return (
            <div
                key={restaurant.id}
                className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 border border-gray-100 h-full transform hover:scale-[1.02] cursor-pointer"
                onClick={() => handleSelectRestaurant(restaurant)}
            >
                <div className={`h-40 bg-gradient-to-r ${gradientClass} relative`}>
                    <div className="absolute top-3 right-3 z-10 flex space-x-2">
                        <button
                            onClick={(e) => handleToggleFavorite(restaurant.id, e)}
                            className={`p-2 rounded-full shadow-sm ${restaurant.isFavorite ? 'bg-white text-red-500' : 'bg-white/90 text-gray-400 hover:text-gray-600'} transition-all duration-200 hover:scale-110`}
                            aria-label={restaurant.isFavorite ? "Eliminar de favoritos" : "Añadir a favoritos"}
                        >
                            <Heart fill={restaurant.isFavorite ? "currentColor" : "none"} size={20} strokeWidth={2} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRestaurant(restaurant);
                            }}
                            className="p-2 rounded-full shadow-sm bg-white/90 text-blue-500 hover:text-blue-600 transition-all duration-200 hover:scale-110"
                            aria-label="Editar restaurante"
                        >
                            <Edit size={20} strokeWidth={2} />
                        </button>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Coffee size={56} className="text-gray-300" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-gray-900/90 via-gray-900/70 to-transparent">
                        <h3 className="text-white font-bold text-lg truncate">{restaurant.name}</h3>
                        <div className="flex items-center mt-1">
                            <Tag size={12} className="text-gray-300 mr-1" />
                            <span className="text-xs text-gray-300">
                                {restaurant.categoryNames?.length || 0} categorías
                            </span>
                        </div>
                    </div>
                </div>
                <div className="p-3">
                    <div className="flex flex-wrap gap-1.5 min-h-[40px]">
                        {restaurant.categoryNames && restaurant.categoryNames.slice(0, 3).map((cat, index) => (
                            <span key={index} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getColorForText(cat)}`}>
                                {cat}
                            </span>
                        ))}
                        {restaurant.categoryNames && restaurant.categoryNames.length > 3 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                +{restaurant.categoryNames.length - 3}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col mt-3 space-y-1">
                        {restaurant.phone && (
                            <div className="flex items-center text-gray-500 text-xs">
                                <Phone size={12} className="mr-1" />
                                <span>{restaurant.phone}</span>
                            </div>
                        )}
                        {restaurant.schedule && (
                            <div className="flex items-center text-gray-500 text-xs">
                                <Clock size={12} className="mr-1" />
                                <span>{restaurant.schedule}</span>
                            </div>
                        )}
                        <div className="flex items-center text-gray-500 text-xs">
                            <MapPin size={12} className="mr-1" />
                            <span>
                                {restaurant.location ?
                                    `${restaurant.location.lat.toFixed(4)}, ${restaurant.location.lng.toFixed(4)}` :
                                    "Ubicación no especificada"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Renderizar fila de restaurante (vista de lista)
    const renderRestaurantRow = (restaurant) => {
        return (
            <div
                key={restaurant.id}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150 cursor-pointer"
                onClick={() => handleSelectRestaurant(restaurant)}
            >
                <div className="flex items-center w-full sm:w-auto mb-3 sm:mb-0">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getCardGradient(restaurant.id)} flex items-center justify-center mr-3 text-gray-500`}>
                        <Coffee size={24} />
                    </div>
                    <div>
                        <div className="font-medium text-gray-900">{restaurant.name}</div>
                        <div className="flex flex-col text-xs text-gray-500 mt-1">
                            {restaurant.phone && (
                                <div className="flex items-center">
                                    <Phone size={12} className="mr-1" /> {restaurant.phone}
                                </div>
                            )}
                            {restaurant.schedule && (
                                <div className="flex items-center">
                                    <Clock size={12} className="mr-1" /> {restaurant.schedule}
                                </div>
                            )}
                            <div className="flex items-center">
                                <MapPin size={12} className="mr-1" />
                                {restaurant.location ?
                                    `${restaurant.location.lat.toFixed(4)}, ${restaurant.location.lng.toFixed(4)}` :
                                    "Ubicación no especificada"}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1 w-full sm:w-auto mb-3 sm:mb-0 sm:mx-4">
                    {restaurant.categoryNames && restaurant.categoryNames.slice(0, 2).map((cat, index) => (
                        <span key={index} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getColorForText(cat)}`}>
                            {cat}
                        </span>
                    ))}
                    {restaurant.categoryNames && restaurant.categoryNames.length > 2 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                            +{restaurant.categoryNames.length - 2}
                        </span>
                    )}
                </div>

                <div className="flex items-center justify-between w-full sm:w-auto">
                    <div className="text-xs text-gray-600 flex items-center">
                        <PieChart size={12} className="mr-1" />
                        {restaurant.subcategoryNames?.length || 0} subcategorías
                    </div>

                    <div className="flex ml-4 space-x-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectRestaurant(restaurant);
                            }}
                            className="p-2 rounded-full text-blue-500 hover:bg-blue-50 transition-colors duration-200"
                            aria-label="Editar restaurante"
                        >
                            <Edit size={18} />
                        </button>
                        <button
                            onClick={(e) => handleToggleFavorite(restaurant.id, e)}
                            className={`p-2 rounded-full ${restaurant.isFavorite ? 'text-red-500 hover:bg-red-50' : 'text-gray-400 hover:bg-gray-100'} transition-colors duration-200`}
                            aria-label={restaurant.isFavorite ? "Eliminar de favoritos" : "Añadir a favoritos"}
                        >
                            <Heart fill={restaurant.isFavorite ? "currentColor" : "none"} size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Categorías horizontales para filtro rápido
    const renderCategoryPills = () => {
        return (
            <div className="mb-4 overflow-x-auto pb-1 -mx-1 px-1 hide-scrollbar">
                <div className="flex space-x-2">
                    {categories.slice(0, 10).map(category => (
                        <button
                            key={category.id}
                            onClick={() => handleCategorySelect(category.id)}
                            className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors duration-200 border ${selectedCategories.includes(category.id)
                                ? getColorForText(category.name)
                                : 'border-gray-200 bg-white text-gray-700'
                                }`}
                        >
                            {category.name}
                        </button>
                    ))}
                    {categories.length > 10 && (
                        <button
                            onClick={() => setShowCategoryFilter(!showCategoryFilter)}
                            className="px-3 py-1.5 rounded-full text-sm whitespace-nowrap bg-gray-100 text-gray-700 border border-gray-200 flex items-center"
                        >
                            <span>Más</span>
                            <ChevronDown size={14} className="ml-1" />
                        </button>
                    )}
                </div>

                {showCategoryFilter && (
                    <div className="mt-3 bg-white p-3 rounded-xl shadow-md border border-gray-200">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {categories.slice(10).map(category => (
                                <button
                                    key={category.id}
                                    onClick={() => handleCategorySelect(category.id)}
                                    className={`px-3 py-1.5 rounded-lg text-sm text-left transition-colors duration-200 ${selectedCategories.includes(category.id)
                                        ? getColorForText(category.name)
                                        : 'bg-gray-50 text-gray-700'
                                        }`}
                                >
                                    {category.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Modal de filtros para móvil
    const renderFilterModal = () => {
        if (!showFilterModal) return null;

        return (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center sm:items-center p-4">
                <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden shadow-xl">
                    <div className="sticky top-0 z-10 bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">Filtros</h2>
                        <button
                            onClick={() => setShowFilterModal(false)}
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="overflow-y-auto p-4 max-h-[calc(80vh-60px)]">
                        {/* Categorías */}
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                <Tag size={14} className="mr-1.5" />
                                Categorías {selectedCategories.length > 0 && `(${selectedCategories.length})`}
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {categories.map(category => (
                                    <div
                                        key={category.id}
                                        onClick={() => handleCategorySelect(category.id)}
                                        className={`px-3 py-2 rounded-xl text-sm cursor-pointer transition-all duration-200 flex items-center ${selectedCategories.includes(category.id)
                                            ? getColorForText(category.name) + ' shadow-sm border font-medium'
                                            : 'bg-gray-50 text-gray-700 border border-gray-100'
                                            }`}
                                    >
                                        <span className="truncate">{category.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Subcategorías */}
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                <PieChart size={14} className="mr-1.5" />
                                Subcategorías {selectedSubcategories.length > 0 && `(${selectedSubcategories.length})`}
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {subcategories.map(subcategory => (
                                    <div
                                        key={subcategory.id}
                                        onClick={() => handleSubcategorySelect(subcategory.id)}
                                        className={`px-3 py-2 rounded-xl text-sm cursor-pointer transition-all duration-200 flex items-center ${selectedSubcategories.includes(subcategory.id)
                                            ? getColorForText(subcategory.name) + ' shadow-sm border font-medium'
                                            : 'bg-gray-50 text-gray-700 border border-gray-100'
                                            }`}
                                    >
                                        <span className="truncate">{subcategory.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="sticky bottom-0 z-10 bg-white px-4 py-3 border-t border-gray-100 flex justify-between">
                        <button
                            onClick={clearAllFilters}
                            className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                        >
                            Limpiar filtros
                        </button>
                        <button
                            onClick={() => setShowFilterModal(false)}
                            className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Modal de edición/creación de restaurante
    const renderEditModal = () => {
        if (!showEditModal) return null;

        return (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center sm:items-center p-4">
                <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl">
                    <div className="sticky top-0 z-10 bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">
                            {isCreating ? "Crear nuevo restaurante" : "Editar restaurante"}
                        </h2>
                        <button
                            onClick={() => {
                                setShowEditModal(false);
                                setIsCreating(false);
                            }}
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="overflow-y-auto p-4 max-h-[calc(90vh-64px)] custom-scrollbar">
                        {/* Formulario de edición */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                {/* Nombre */}
                                <div className="mb-4">
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={editedRestaurant?.name || ""}
                                        onChange={(e) => handleRestaurantFormChange("name", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Nombre del restaurante"
                                    />
                                </div>

                                {/* Teléfono */}
                                <div className="mb-4">
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                        Teléfono
                                    </label>
                                    <input
                                        type="text"
                                        id="phone"
                                        value={editedRestaurant?.phone || ""}
                                        onChange={(e) => handleRestaurantFormChange("phone", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Número de teléfono"
                                    />
                                </div>

                                {/* Horario */}
                                <div className="mb-4">
                                    <label htmlFor="schedule" className="block text-sm font-medium text-gray-700 mb-1">
                                        Horario
                                    </label>
                                    <input
                                        type="text"
                                        id="schedule"
                                        value={editedRestaurant?.schedule || ""}
                                        onChange={(e) => handleRestaurantFormChange("schedule", e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ej: Lun-Vie 9:00-18:00"
                                    />
                                </div>

                                {/* Ubicación */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ubicación
                                    </label>
                                    <div className="flex flex-col space-y-2">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label htmlFor="lat" className="block text-xs text-gray-500 mb-1">
                                                    Latitud
                                                </label>
                                                <input
                                                    type="text"
                                                    id="lat"
                                                    value={editedRestaurant?.location?.lat || ""}
                                                    onChange={(e) => handleRestaurantFormChange("location", {
                                                        ...editedRestaurant?.location,
                                                        lat: parseFloat(e.target.value) || 0
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="Latitud"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="lng" className="block text-xs text-gray-500 mb-1">
                                                    Longitud
                                                </label>
                                                <input
                                                    type="text"
                                                    id="lng"
                                                    value={editedRestaurant?.location?.lng || ""}
                                                    onChange={(e) => handleRestaurantFormChange("location", {
                                                        ...editedRestaurant?.location,
                                                        lng: parseFloat(e.target.value) || 0
                                                    })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="Longitud"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleOpenMapPicker}
                                            className="px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                        >
                                            Seleccionar en mapa
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div>
                                {/* Categorías */}
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <Tag size={14} className="mr-1.5" />
                                        Categorías {editedRestaurant?.categoryIds?.length > 0 && `(${editedRestaurant.categoryIds.length})`}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto custom-scrollbar">
                                        {categories.map(category => (
                                            <div
                                                key={category.id}
                                                onClick={() => handleEditCategoryToggle(category.id)}
                                                className={`px-3 py-2 rounded-xl text-sm cursor-pointer transition-all duration-200 flex items-center ${editedRestaurant?.categoryIds?.includes(category.id)
                                                    ? getColorForText(category.name) + ' shadow-sm border font-medium'
                                                    : 'bg-gray-50 text-gray-700 border border-gray-100'
                                                    }`}
                                            >
                                                <span className="truncate">{category.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Subcategorías */}
                                <div className="mb-4">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <PieChart size={14} className="mr-1.5" />
                                        Subcategorías {editedRestaurant?.subcategoryIds?.length > 0 && `(${editedRestaurant.subcategoryIds.length})`}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto custom-scrollbar">
                                        {subcategories.map(subcategory => (
                                            <div
                                                key={subcategory.id}
                                                onClick={() => handleEditSubcategoryToggle(subcategory.id)}
                                                className={`px-3 py-2 rounded-xl text-sm cursor-pointer transition-all duration-200 flex items-center ${editedRestaurant?.subcategoryIds?.includes(subcategory.id)
                                                    ? getColorForText(subcategory.name) + ' shadow-sm border font-medium'
                                                    : 'bg-gray-50 text-gray-700 border border-gray-100'
                                                    }`}
                                            >
                                                <span className="truncate">{subcategory.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Vista previa del mapa */}
                        <div className="mt-6 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="h-64">
                                <LoadScript googleMapsApiKey="AIzaSyBSXE8zZ-SqMWdDz16hr00Djvkm3mMit28">
                                    <GoogleMap
                                        mapContainerStyle={{ width: "100%", height: "100%" }}
                                        center={editedRestaurant?.location || mapCenter}
                                        zoom={15}
                                        onClick={handleMapClick}
                                    >
                                        {tempMarkerPosition && (
                                            <Marker position={tempMarkerPosition} />
                                        )}
                                    </GoogleMap>
                                </LoadScript>
                            </div>
                            <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500">
                                Click en el mapa para actualizar la ubicación
                            </div>
                        </div>
                    </div>

                    <div className="sticky bottom-0 z-10 bg-white px-4 py-3 border-t border-gray-100 flex justify-between">
                        {!isCreating && (
                            <button
                                onClick={handleDeleteRestaurant}
                                className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors duration-200 flex items-center"
                            >
                                <Trash size={16} className="mr-1.5" />
                                Eliminar
                            </button>
                        )}
                        <div className="flex space-x-2 ml-auto">
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setIsCreating(false);
                                }}
                                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveRestaurant}
                                className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200 flex items-center"
                            >
                                <Save size={16} className="mr-1.5" />
                                {isCreating ? "Crear" : "Guardar"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Modal para seleccionar ubicación en mapa grande
    const renderMapPickerModal = () => {
        if (!showMapPicker) return null;

        return (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Seleccionar ubicación
                        </h2>
                        <button
                            onClick={handleCloseMapPicker}
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="h-[70vh]">
                        <LoadScript googleMapsApiKey="AIzaSyBSXE8zZ-SqMWdDz16hr00Djvkm3mMit28">
                            <GoogleMap
                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                center={editedRestaurant?.location || mapCenter}
                                zoom={14}
                                onClick={handleMapClick}
                            >
                                {tempMarkerPosition && (
                                    <Marker position={tempMarkerPosition} />
                                )}
                            </GoogleMap>
                        </LoadScript>
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                            Click en el mapa para actualizar la ubicación
                        </div>
                        <button
                            onClick={handleCloseMapPicker}
                            className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                        >
                            Confirmar ubicación
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 bg-gray-50 min-h-screen">
            {/* Header Bar */}
            <div className="sticky top-0 z-30 bg-white shadow-sm pt-4 pb-3 -mx-4 px-4 sm:static sm:shadow-none sm:p-0 rounded-b-xl">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-xl font-bold text-gray-800 truncate">
                        Explorador de Restaurantes
                    </h1>

                    <div className="flex space-x-2">
                        <button
                            onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                            className="p-2 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200"
                            aria-label={viewMode === "grid" ? "Cambiar a vista de lista" : "Cambiar a vista de cuadrícula"}
                        >
                            {viewMode === "grid" ? (
                                <List size={20} />
                            ) : (
                                <Grid size={20} />
                            )}
                        </button>

                        <button
                            onClick={() => setShowFilterModal(true)}
                            className={`p-2 rounded-xl ${showFilters ? 'bg-blue-50 text-blue-600 border-blue-200 border' : 'bg-white border text-gray-600 hover:bg-gray-50 active:bg-gray-100'} transition-colors duration-200 relative`}
                            aria-label="Filtros"
                        >
                            <Filter size={20} />
                            {(selectedCategories.length > 0 || selectedSubcategories.length > 0) && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                                    {selectedCategories.length + selectedSubcategories.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Barra de búsqueda */}
                <div className="mb-4">
                    <div className="relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar restaurantes..."
                            className="w-full px-4 py-3 pl-10 pr-10 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow duration-200"
                        />
                        <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                                aria-label="Limpiar búsqueda"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex mb-2 overflow-x-auto hide-scrollbar pb-1 -mx-1 px-1">
                    <button
                        onClick={() => setSelectedTab("all")}
                        className={`py-2 px-4 rounded-xl font-medium text-sm whitespace-nowrap transition-colors duration-200 ${selectedTab === "all"
                            ? "bg-blue-50 text-blue-600 border border-blue-200"
                            : "bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-gray-100"
                            }`}
                    >
                        Todos los restaurantes
                    </button>
                    <button
                        onClick={() => setSelectedTab("favorites")}
                        className={`ml-2 py-2 px-4 rounded-xl font-medium text-sm flex items-center whitespace-nowrap transition-colors duration-200 ${selectedTab === "favorites"
                            ? "bg-red-50 text-red-600 border border-red-200"
                            : "bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-gray-100"
                            }`}
                    >
                        <Heart size={16} className="mr-1.5" />
                        Favoritos
                        {favorites.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 text-xs">
                                {favorites.length}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Filtro rápido de categorías */}
            {!isLoading && !error && categories.length > 0 && (
                renderCategoryPills()
            )}

            {/* Estadísticas tipo tarjetas */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-xl shadow-sm text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white/80">Restaurantes</p>
                            <p className="text-lg font-bold">{stats.totalRestaurants}</p>
                        </div>
                        <Award size={24} className="text-white/70" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 rounded-xl shadow-sm text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white/80">Categorías</p>
                            <p className="text-lg font-bold">{stats.totalCategories}</p>
                        </div>
                        <Tag size={24} className="text-white/70" />
                    </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-3 rounded-xl shadow-sm text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-white/80">Subcategorías</p>
                            <p className="text-lg font-bold">{stats.totalSubcategories}</p>
                        </div>
                        <PieChart size={24} className="text-white/70" />
                    </div>
                </div>
            </div>

            {/* Botón para crear nuevo restaurante */}
            <div className="mb-4">
                <button
                    onClick={handleCreateNewRestaurant}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl px-4 py-2 flex items-center transition-colors duration-200"
                >
                    <Plus size={18} className="mr-2" />
                    Crear nuevo restaurante
                </button>
            </div>

            {/* Estado de carga */}
            {isLoading && (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="w-16 h-16 relative">
                        <div className="absolute inset-0 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Coffee size={20} className="text-blue-500" />
                        </div>
                    </div>
                    <p className="text-gray-600 font-medium mt-4">Cargando restaurantes...</p>
                    <p className="text-gray-500 text-sm mt-1">Esto puede tardar unos segundos</p>
                </div>
            )}

            {/* Mensaje de error */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium">{error}</p>
                            <p className="text-xs mt-1">Por favor, inténtalo de nuevo más tarde o contacta con soporte.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Mensaje sin resultados */}
            {!isLoading && !error && filteredRestaurants.length === 0 && (
                <div className="text-center py-12 px-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
                        <Info size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron restaurantes</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                        No hay restaurantes que coincidan con tus criterios de búsqueda. Prueba ajustando los filtros o realizando una búsqueda diferente.
                    </p>
                    <button
                        onClick={clearAllFilters}
                        className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                        <RefreshCw size={16} className="mr-2" strokeWidth={2} />
                        Limpiar filtros
                    </button>
                </div>
            )}

            {/* Lista de restaurantes (Vista Grid) */}
            {!isLoading && !error && filteredRestaurants.length > 0 && viewMode === "grid" && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {currentItems.map(restaurant => renderRestaurantCard(restaurant))}
                </div>
            )}

            {/* Lista de restaurantes (Vista Lista) */}
            {!isLoading && !error && filteredRestaurants.length > 0 && viewMode === "list" && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <div className="divide-y divide-gray-100">
                        {currentItems.map(restaurant => renderRestaurantRow(restaurant))}
                    </div>
                </div>
            )}

            {/* Paginación */}
            {!isLoading && !error && filteredRestaurants.length > 0 && totalPages > 1 && (
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between">
                    <div className="text-sm text-gray-500 mb-4 sm:mb-0">
                        <span className="font-medium">{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredRestaurants.length)}</span> de{" "}
                        <span className="font-medium">{filteredRestaurants.length}</span> restaurantes
                    </div>

                    <div className="flex space-x-1">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-2 border rounded-xl text-sm flex items-center ${currentPage === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                                } transition-colors duration-200`}
                            aria-label="Página anterior"
                        >
                            <ChevronLeft size={16} className="mr-1" />
                            Anterior
                        </button>

                        {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                            let pageNumber;
                            if (totalPages <= 5) {
                                pageNumber = index + 1;
                            } else if (currentPage <= 3) {
                                pageNumber = index + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + index;
                            } else {
                                pageNumber = currentPage - 2 + index;
                            }

                            return (
                                <button
                                    key={pageNumber}
                                    onClick={() => paginate(pageNumber)}
                                    className={`w-9 h-9 flex items-center justify-center border rounded-xl text-sm ${currentPage === pageNumber
                                        ? 'bg-blue-50 text-blue-600 border-blue-200 font-medium'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                                        } transition-colors duration-200`}
                                    aria-label={`Ir a página ${pageNumber}`}
                                >
                                    {pageNumber}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-2 border rounded-xl text-sm flex items-center ${currentPage === totalPages
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                                } transition-colors duration-200`}
                            aria-label="Página siguiente"
                        >
                            Siguiente
                            <ChevronRight size={16} className="ml-1" />
                        </button>
                    </div>
                </div>
            )}

            {/* Botones flotantes para móvil */}
            <div className="fixed bottom-6 right-6 z-20 flex flex-col space-y-2">
                {showScrollTop && (
                    <button
                        onClick={scrollToTop}
                        className="p-3 rounded-full shadow-lg bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200"
                        aria-label="Volver arriba"
                    >
                        <ArrowUp size={20} />
                    </button>
                )}

                <button
                    onClick={handleCreateNewRestaurant}
                    className="p-3 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200"
                    aria-label="Crear nuevo restaurante"
                >
                    <Plus size={20} />
                </button>

                <button
                    onClick={() => setShowFilterModal(true)}
                    className={`p-3 rounded-full shadow-lg ${(selectedCategories.length > 0 || selectedSubcategories.length > 0)
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                        } transition-colors duration-200 relative`}
                    aria-label="Filtros"
                >
                    <Filter size={20} />
                    {(selectedCategories.length > 0 || selectedSubcategories.length > 0) && (
                        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                            {selectedCategories.length + selectedSubcategories.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Modal de filtros */}
            {renderFilterModal()}

            {/* Modal de edición */}
            {renderEditModal()}

            {/* Modal de selección de mapa */}
            {renderMapPickerModal()}

            {/* Estilos adicionales */}
            <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        
        @media (max-width: 640px) {
          .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
          }
        }
      `}</style>
        </div>
    );
};

export default RestBack;